import axios, { AxiosError, AxiosResponse } from 'axios';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { InternalAxiosRequestConfig } from 'axios';
import { useDispatch } from 'react-redux';
import { HttpResponse } from '@/typings/request';
import { resolveApiBaseUrl } from './apiBaseUrl';
const instance = axios.create({
    baseURL: resolveApiBaseUrl(),
    // Logo upload + Mandrill email on create can exceed 10s; short timeouts show as "(canceled)" in DevTools.
    timeout: 60000,
});
instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        config.baseURL = resolveApiBaseUrl();
        const token = sessionStorage.getItem('faastaa-admin-satoken');
        if (token) {
            config.headers.set('faastaa-admin-satoken', token);
        }
        config.headers.set('Accept-Language', 'en-US');
        config.headers.set('x-timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
        return config;
    },
    error => {
        Promise.reject(error);
    }
);

const accountErrorCodes = [5000, 5001, 5002, 5003, 5004];

const AxiosInterceptor = ({ children }: any) => {
    const navigate = useNavigate();
    const resInterceptor = (response: AxiosResponse<HttpResponse>) => {
        const code = response.data.code;
        if (response.headers['faastaa-admin-satoken'])
            sessionStorage.setItem('faastaa-admin-satoken', response.headers['faastaa-admin-satoken']);
        if (code !== 200) {
            if (accountErrorCodes.includes(code)) {
                sessionStorage.clear();
                navigate('/login');
            }
            return Promise.reject(response.data.message);
        } else {
            return response.data;
        }
    };

    const errInterceptor = (error: AxiosError) => {
        const isTimeOut = error.code === 'ECONNABORTED' && error.message.indexOf('timeout') !== -1;
        return Promise.reject(
            error.message === 'Network Error' || isTimeOut
                ? 'Looks like the connection lost. Please try again in a moment.'
                : error.message
        );
    };

    useEffect(() => {
        const interceptor = instance.interceptors.response.use(resInterceptor as never, errInterceptor);
        return () => instance.interceptors.response.eject(interceptor);
    }, []);

    return children;
};

export default instance;
export { AxiosInterceptor };
